import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Portal from '../../components/Portal';
import {
  partsHeureReunion,
  todayISOReunion,
  formatHeureReunion,
  reunionWallClockToDate,
} from '../../utils/reunionTime';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const OVERLAY_CLASS =
  'fixed top-0 left-0 z-[55] flex h-[100dvh] w-screen items-center justify-center bg-black/50 p-4';
const OVERLAY_CLASS_DARK =
  'fixed top-0 left-0 z-[60] flex h-[100dvh] w-screen items-center justify-center bg-black/60 p-4';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

const MINUTES_QUARTS = ['00', '15', '30', '45'];
const HEURES_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0')
);

/** Parse "HH:mm" ou "HH:mm:ss" → { h, m } avec minutes limitées aux quarts d'heure */
function parseHeureQuarts(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return { h: '09', m: '00' };
  }
  const parts = timeStr.trim().split(':');
  const hNum = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0));
  const h = String(hNum).padStart(2, '0');
  let m = (parts[1] || '00').slice(0, 2);
  if (!MINUTES_QUARTS.includes(m)) {
    m = '00';
  }
  return { h, m };
}

function joinHeure(h, m) {
  return `${h}:${m}`;
}

/** Minutes depuis minuit (même logique que les select de la modale). */
function heureHHMMVersMinutes(hhmm) {
  const { h, m } = parseHeureQuarts(hhmm || '');
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

function heureFinEstStrictementApresDebut(heureDebut, heureFin) {
  return heureHHMMVersMinutes(heureFin) > heureHHMMVersMinutes(heureDebut);
}

function plagesHorairesSeChevauchent(aDebut, aFin, bDebut, bFin) {
  return (
    heureHHMMVersMinutes(aDebut) < heureHHMMVersMinutes(bFin) &&
    heureHHMMVersMinutes(aFin) > heureHHMMVersMinutes(bDebut)
  );
}

function jourISO(plage) {
  const j = plage?.jour ?? plage?.date;
  if (!j) return '';
  if (typeof j === 'string') return j.split('T')[0];
  try {
    return new Date(j).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

function debutPlage(plage) {
  return plage?.heureOuvertureDebut ?? plage?.heureDebut ?? '';
}

function finPlage(plage) {
  return plage?.heureOuvertureFin ?? plage?.heureFin ?? '';
}

function prestationsProposeesPlage(plage) {
  return plage?.prestationsProposees ?? plage?.servicesOuverts;
}

/** Aligné sur CalendrierReservations / schéma réservation */
const TACHES_SERVICES = [
  {
    value: 'depot',
    label: 'Dépôt de hausses',
    title: '1 réservation par jour (tous créneaux) ; hausses déposées à préciser',
  },
  { value: 'candi', label: 'Fabrication de candi', title: '3 réservations par créneau ; sacs de sucre 25 kg à moudre' },
  { value: 'extraction_hausses', label: 'Extraction des hausses', title: '1 réservation par jour ; quantité de hausses à extraire' },
  { value: 'cire', label: 'Gaufrage de cire', title: '1 réservation par jour ; kg de cire à gaufrer (feuille alvéolée)' },
  {
    value: 'conditionnement',
    label: 'Conditionnement de miel',
    title: '1 réservation par jour ; kg de miel à conditionner (tarif à la tranche de 5 kg)',
  },
];

const TACHES_SERVICES_ACTIVES = TACHES_SERVICES.filter((t) => t.disponible !== false);
const ALL_SERVICE_VALUES = TACHES_SERVICES_ACTIVES.map((t) => t.value);

function prestationsSansIndisponibles(values) {
  const masquees = new Set(TACHES_SERVICES.filter((t) => t.disponible === false).map((t) => t.value));
  return (values || []).filter((v) => !masquees.has(v));
}

function labelPrestationType(type) {
  if (type === 'extraction') return 'Extraction des hausses';
  return TACHES_SERVICES.find((t) => t.value === type)?.label || type;
}

/** Unités courtes, alignées sur le formulaire de réservation. */
const UNITES_QUANTITE = {
  depot: { un: 'hausse', plusieurs: 'hausses' },
  extraction: { un: 'hausse', plusieurs: 'hausses' },
  extraction_hausses: { un: 'hausse', plusieurs: 'hausses' },
  cire: { un: 'kg', plusieurs: 'kg' },
  conditionnement: { un: 'kg', plusieurs: 'kg' },
  candi: { un: 'sac de 25 kg', plusieurs: 'sacs de 25 kg' },
};

function libelleQuantiteEntreParentheses(type, quantite) {
  if (quantite == null || quantite === '') return '';
  const n = Number(quantite);
  const unite = UNITES_QUANTITE[type];
  if (!unite || Number.isNaN(n)) return ` (${quantite})`;
  return ` (${n} ${n === 1 ? unite.un : unite.plusieurs})`;
}

function toISODateLocal(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Jour courant à La Réunion, format YYYY-MM-DD — comparables par ordre lexicographique. */
function todayISOLocal() {
  return todayISOReunion();
}

function isDateBeforeToday(dayDate) {
  if (!dayDate) return false;
  return toISODateLocal(dayDate) < todayISOLocal();
}

function isISODateStrictlyPast(iso) {
  if (!iso || typeof iso !== 'string') return false;
  return iso.split('T')[0] < todayISOLocal();
}

/** Plage dont l'heure de fin est déjà passée à La Réunion (même si c'est aujourd'hui). */
function isSlotEndTimePast(slot) {
  const iso = jourISO(slot);
  if (!iso) return false;
  const fin = finPlage(slot);
  if (!fin) return isISODateStrictlyPast(iso);
  const endDt = reunionWallClockToDate(iso.split('T')[0], fin, { endOfMinute: true });
  if (!endDt) return isISODateStrictlyPast(iso);
  return endDt < new Date();
}

const TIMELINE_PX_PER_HOUR = 88;

function plageBoundsMinutes(plage) {
  const d = debutPlage(plage);
  const f = finPlage(plage);
  if (!d || !f) {
    return { startM: 7 * 60, endM: 20 * 60 };
  }
  return {
    startM: heureHHMMVersMinutes(d),
    endM: heureHHMMVersMinutes(f),
  };
}

function hourRangePourPlages(plages) {
  const DEFAULT_START = 7;
  const DEFAULT_END = 20;
  let startHour = DEFAULT_START;
  let endHour = DEFAULT_END;
  for (const p of plages) {
    const b = plageBoundsMinutes(p);
    if (b.startM != null) startHour = Math.min(startHour, Math.floor(b.startM / 60));
    if (b.endM != null) endHour = Math.max(endHour, Math.ceil(b.endM / 60));
  }
  startHour = Math.max(0, startHour);
  endHour = Math.min(24, Math.max(startHour + 1, endHour));
  return { startHour, endHour };
}

/** Cases Date pour la grille mensuelle (jours hors mois inclus, grisés côté UI). */
function getMonthCalendarCells(year, month0) {
  const first = new Date(year, month0, 1);
  const lastDay = new Date(year, month0 + 1, 0).getDate();
  const jsDow = first.getDay();
  const mondayOffset = jsDow === 0 ? 6 : jsDow - 1;
  const cells = [];
  for (let i = mondayOffset; i > 0; i--) {
    cells.push(new Date(year, month0, 1 - i));
  }
  for (let d = 1; d <= lastDay; d++) {
    cells.push(new Date(year, month0, d));
  }
  let next = 1;
  while (cells.length % 7 !== 0) {
    cells.push(new Date(year, month0 + 1, next++));
  }
  return cells;
}

/* ─── Helpers gestion réservations ──────────────────────────────────────── */

function prestationGenereTarif(t) {
  if (!t?.type) return false;
  return ['extraction', 'cire', 'candi', 'extraction_hausses', 'conditionnement'].includes(t.type);
}

function normaliseStatutResa(statut) {
  if (statut == null || statut === '') return '';
  return String(statut).trim().toLowerCase();
}

function classeStatutResa(statut) {
  const s = normaliseStatutResa(statut);
  if (s === 'reservee') return 'bg-blue-100 text-blue-800';
  if (s === 'honoree') return 'bg-green-100 text-green-800';
  if (s === 'annulee') return 'bg-red-50 text-red-700';
  if (s === 'absente') return 'bg-honey-100 text-honey-900';
  return 'bg-slate-100 text-slate-700';
}

function euros2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/** Dette encore ouverte, avec une partie déjà réglée (prépaiement ou encaissement partiel). */
function detailReglementDette(r) {
  const p = r?.paiement;
  if (p?.statut !== 'dette') return null;
  const total = euros2(r?.montantTotal);
  const prepaye = euros2(r?.montantPrepaye);
  const aUnReste = p.montantRestantDette != null && p.montantRestantDette !== '';
  const restant = aUnReste
    ? euros2(p.montantRestantDette)
    : euros2(Math.max(0, total - prepaye));
  const dejaPaye = euros2(Math.max(0, total - restant));
  if (!(restant > 0) || !(dejaPaye > 0)) return null;
  return { total, restant, dejaPaye, note: p.note || '' };
}

function libelleStatutResa(s) {
  const key = normaliseStatutResa(s);
  const m = {
    reservee: 'Réservée (à confirmer)',
    honoree: 'Réalisée',
    annulee: 'Annulée',
    absente: 'Absent·e',
  };
  return m[key] || s;
}

function pastillesPaiementResa(r) {
  const p = r?.paiement || {};
  const methodes = {
    cheque: 'Chèque',
    espece: 'Espèces',
    stripe: 'En ligne',
    solde: 'Avoir',
    virement: 'Virement',
  };
  const pastilles = [];
  const classe = 'border border-slate-200 text-slate-800';
  if (p.statut === 'paye' || (p.methode && p.statut !== 'en_attente' && p.statut !== 'dette')) {
    if (p.methode && methodes[p.methode]) {
      pastilles.push({ label: methodes[p.methode], className: classe });
    } else if (p.statut === 'paye') {
      pastilles.push({ label: 'Réglé', className: classe });
    }
  } else if (p.methode && methodes[p.methode] && p.statut === 'dette') {
    pastilles.push({ label: methodes[p.methode], className: classe });
  }
  if (p.statut === 'dette') {
    pastilles.push({
      label: detailReglementDette(r) ? 'Mise en dette · partiellement payé' : 'Mise en dette',
      className: classe,
    });
  }
  if (p.methode === 'cheque') {
    pastilles.push({
      label: p.chequeStatut === 'encaisse' ? 'Encaissé' : 'Non encaissé',
      className:
        p.chequeStatut === 'encaisse'
          ? 'bg-green-100 text-green-800'
          : 'bg-amber-100 text-amber-900',
    });
  }
  return pastilles;
}

function PastillesReservation({ reservation }) {
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <span
        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${classeStatutResa(reservation?.statut)}`}
      >
        {libelleStatutResa(reservation?.statut)}
      </span>
      {pastillesPaiementResa(reservation).map((pastille) => (
        <span
          key={pastille.label}
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${pastille.className}`}
        >
          {pastille.label}
        </span>
      ))}
    </span>
  );
}

function lignesPrestationReservation(r) {
  const a = r?.prestations;
  const b = r?.taches;
  if (Array.isArray(a) && a.length > 0) return a;
  if (Array.isArray(b) && b.length > 0) return b;
  return [];
}

function lignePrestation(r) {
  const t = lignesPrestationReservation(r)[0];
  if (!t) return '—';
  const label = labelPrestationType(t.type);
  const q = ['depot', 'extraction', 'cire', 'candi', 'extraction_hausses', 'conditionnement'].includes(t.type)
    ? libelleQuantiteEntreParentheses(t.type, t.quantitePrevue)
    : '';
  return `${label}${q}`;
}

function normaliserOrigineRucher(tache = {}) {
  if (['mon', 'sar', 'autre'].includes(tache.origineRucher)) {
    return {
      origineRucher: tache.origineRucher,
      autreRucherPrecision:
        tache.origineRucher === 'autre'
          ? String(tache.autreRucherPrecision || '').trim()
          : '',
    };
  }
  return {
    origineRucher: tache.pourAutreRucher ? 'autre' : 'mon',
    autreRucherPrecision: String(tache.autreRucherPrecision || '').trim(),
  };
}

function libelleOrigineRucher(tache = {}) {
  const { origineRucher, autreRucherPrecision } = normaliserOrigineRucher(tache);
  if (origineRucher === 'sar') return 'Un rucher géré par le SAR';
  if (origineRucher === 'autre') {
    return autreRucherPrecision
      ? `Autre rucher : ${autreRucherPrecision}`
      : 'Autre rucher';
  }
  return 'Mon rucher';
}

function reservationPeutEtreGeree(r) {
  const s = normaliseStatutResa(r?.statut);
  return s === 'reservee';
}

function jourADesReservationsAGerer(daySlots) {
  return daySlots.some((slot) => {
    const sc = slot.statusCounts || {};
    return (sc.reservee || 0) > 0;
  });
}

function countReservationsAGerer(daySlots) {
  return daySlots.reduce((sum, slot) => {
    const sc = slot.statusCounts || {};
    return sum + (sc.reservee || 0);
  }, 0);
}

function reservationEstTerminee(r) {
  const s = normaliseStatutResa(r?.statut);
  return s === 'annulee' || s === 'honoree' || s === 'absente';
}

/* ─────────────────────────────────────────────────────────────────────────── */

export default function GestionCreneaux({ canAssignResponsable = false, canManageActivite = true }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [listeResponsables, setListeResponsables] = useState([]);
  const [responsableUserId, setResponsableUserId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => partsHeureReunion().month - 1);
  const [selectedYear, setSelectedYear] = useState(() => partsHeureReunion().year);
  /** Modale motif si la plage a des réservations actives */
  const [cancelPlageTarget, setCancelPlageTarget] = useState(null);
  const [annulationMotif, setAnnulationMotif] = useState('');
  /** Confirmation retrait plage sans réservation (remplace window.confirm) */
  const [confirmRetraitPlageSlot, setConfirmRetraitPlageSlot] = useState(null);
  const [retraitSansResaSubmitting, setRetraitSansResaSubmitting] = useState(false);

  /** Panneau latéral réservations du jour */
  const [dayResasModal, setDayResasModal] = useState(null);
  // shape: { iso, label, loading: bool, data: Array<{ slot, reservations }>, originalSlots: [] } | null
  /** Jour sélectionné (ISO YYYY-MM-DD) — colonne latérale */
  const [selectedMonthDayIso, setSelectedMonthDayIso] = useState(null);
  /** Vue planning : jour (défaut, comme l’espace adhérent) ou calendrier mensuel */
  const [planningView, setPlanningView] = useState('jour');
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => partsHeureReunion().month - 1);
  const [pickerYear, setPickerYear] = useState(() => partsHeureReunion().year);
  const [pickerSlots, setPickerSlots] = useState([]);
  /** Réservations passées (≤ J-1) encore à gérer */
  const [pendingPastResas, setPendingPastResas] = useState({ count: 0, firstIso: null });

  /** Modale gestion d'une réservation individuelle */
  const [gestionReservation, setGestionReservation] = useState(null);
  const [gestionModalStep, setGestionModalStep] = useState(1);
  const [raisonAnnulationGestion, setRaisonAnnulationGestion] = useState('');
  const [gestionSubmitting, setGestionSubmitting] = useState(false);
  const [gestionModalError, setGestionModalError] = useState('');
  const [step3Quantite, setStep3Quantite] = useState('');
  const [step3Horaire, setStep3Horaire] = useState('');
  const [step3Notes, setStep3Notes] = useState('');
  const [step3ModePaiement, setStep3ModePaiement] = useState('espece');
  const [step3NotePaiement, setStep3NotePaiement] = useState('');
  const [step3RefCheque, setStep3RefCheque] = useState('');
  const [previewMontantTotal, setPreviewMontantTotal] = useState(null);
  const [previewMontantLoading, setPreviewMontantLoading] = useState(false);
  const [previewMontantPrepaye, setPreviewMontantPrepaye] = useState(0);
  const [previewDejaPayeEnLigne, setPreviewDejaPayeEnLigne] = useState(false);
  const [previewEcart, setPreviewEcart] = useState(null);
  const [showCautionFromGestion, setShowCautionFromGestion] = useState(false);
  const [cautionForm, setCautionForm] = useState({ dateReception: '', note: '', document: null });
  const [cautionSubmitting, setCautionSubmitting] = useState(false);
  const [cautionError, setCautionError] = useState('');

  const [formData, setFormData] = useState({
    date: '',
    heureDebut: '09:00',
    heureFin: '12:00',
    notes: '',
    servicesOuverts: [...ALL_SERVICE_VALUES],
  });

  useEffect(() => {
    fetchSlots();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  /** Compte les réservations passées (jour < aujourd’hui) encore à gérer */
  const fetchPendingPastResas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const now = new Date();
      const years = [now.getFullYear(), now.getFullYear() - 1];

      const responses = await Promise.all(
        years.map((y) =>
          axios.get(api(`/miellerie/reservations?annee=${y}&statut=reservee`), { headers })
        )
      );
      const today = todayISOLocal();
      const pending = responses
        .flatMap((r) => r.data || [])
        .map((resa) => {
          const plage = resa.slot || resa.plageOuverture;
          const iso = jourISO(plage);
          return { resa, iso };
        })
        .filter(({ iso }) => iso && iso < today)
        .sort((a, b) => a.iso.localeCompare(b.iso));

      setPendingPastResas({
        count: pending.length,
        firstIso: pending[0]?.iso || null,
      });
    } catch {
      setPendingPastResas({ count: 0, firstIso: null });
    }
  }, []);

  useEffect(() => {
    fetchPendingPastResas();
  }, [fetchPendingPastResas, slots]);

  useEffect(() => {
    if (!showModal || !canAssignResponsable || editingSlot) {
      if (!showModal || editingSlot) {
        setListeResponsables([]);
      }
      return;
    }

    const token = localStorage.getItem('token');
    const annee = formData.date
      ? new Date(`${formData.date}T12:00:00`).getFullYear()
      : selectedYear;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(api(`/miellerie/responsables?annee=${annee}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setListeResponsables(data);
        if (data.length > 0) {
          const firstId = data[0].user?._id || data[0].user;
          setResponsableUserId(String(firstId));
        } else {
          setResponsableUserId('');
        }
      } catch {
        if (!cancelled) {
          setListeResponsables([]);
          setResponsableUserId('');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showModal, canAssignResponsable, editingSlot, formData.date, selectedYear]);

  /** Preview du montant (step 3) — debounce 320 ms */
  useEffect(() => {
    if (gestionModalStep !== 3 || !gestionReservation) return;
    const t0 = (gestionReservation.prestations || gestionReservation.taches || [])[0];
    if (!t0 || !prestationGenereTarif(t0)) {
      setPreviewMontantTotal(null);
      setPreviewMontantPrepaye(0);
      setPreviewDejaPayeEnLigne(false);
      setPreviewEcart(null);
      return;
    }
    const q = Math.floor(Number(step3Quantite));
    if (Number.isNaN(q) || q < 0) {
      setPreviewMontantTotal(null);
      setPreviewEcart(null);
      return;
    }
    setPreviewMontantLoading(true);
    const tmr = setTimeout(async () => {
      let cancelled = false;
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.post(
          api(`/miellerie/reservations/${gestionReservation._id}/preview-montant`),
          { prestations: [{ type: t0.type, quantiteEffective: q }] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) {
          setPreviewMontantTotal(typeof data.montantTotal === 'number' ? data.montantTotal : 0);
          setPreviewMontantPrepaye(typeof data.montantPrepaye === 'number' ? data.montantPrepaye : 0);
          setPreviewDejaPayeEnLigne(!!data.dejaPayeEnLigne);
          setPreviewEcart(typeof data.ecart === 'number' ? data.ecart : null);
        }
      } catch {
        if (!cancelled) {
          setPreviewMontantTotal(null);
          setPreviewEcart(null);
        }
      } finally {
        if (!cancelled) setPreviewMontantLoading(false);
      }
      return () => { cancelled = true; };
    }, 320);
    return () => clearTimeout(tmr);
  }, [gestionModalStep, gestionReservation, step3Quantite]);

  const fetchSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/slots?annee=${selectedYear}&mois=${selectedMonth + 1}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlots(data);
      setLoading(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Erreur lors du chargement des horaires d\'ouverture'
      );
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const servicesOuverts = prestationsSansIndisponibles(
      formData.servicesOuverts && formData.servicesOuverts.length > 0
        ? formData.servicesOuverts
        : []
    );
    if (servicesOuverts.length === 0) {
      toast.error('Cochez au moins une prestation proposée sur cette plage d\'ouverture');
      return;
    }

    if (isISODateStrictlyPast(formData.date)) {
      toast.error('Les dates passées ne peuvent pas être utilisées pour une plage d\'ouverture.');
      return;
    }

    if (editingSlot && isSlotEndTimePast(editingSlot)) {
      toast.error('Impossible de modifier une plage dont l\'heure de fin est passée.');
      return;
    }

    if (!heureFinEstStrictementApresDebut(formData.heureDebut, formData.heureFin)) {
      toast.error("L'heure de fin doit être strictement après l'heure de début.");
      return;
    }

    const chevauchements = slots.filter((s) => {
      if (editingSlot && String(s._id) === String(editingSlot._id)) return false;
      if (s.statut === 'annule' || s.statut === 'annulee') return false;
      if (jourISO(s) !== formData.date) return false;
      return plagesHorairesSeChevauchent(
        formData.heureDebut,
        formData.heureFin,
        debutPlage(s),
        finPlage(s)
      );
    });
    if (chevauchements.length > 0) {
      const details = chevauchements
        .map((s) => {
          const r = s.responsable;
          const nom =
            r && typeof r === 'object'
              ? [r.prenom, r.nom].filter(Boolean).join(' ')
              : '';
          return `${debutPlage(s)}–${finPlage(s)}${nom ? ` (${nom})` : ''}`;
        })
        .join(', ');
      toast.error(
        `Cette plage chevauche une ouverture déjà prévue : ${details}. Un seul responsable peut être présent à une heure donnée.`
      );
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        jour: formData.date,
        heureOuvertureDebut: formData.heureDebut,
        heureOuvertureFin: formData.heureFin,
        notes: formData.notes,
        prestationsProposees: servicesOuverts,
      };

      if (editingSlot) {
        await axios.put(
          api(`/miellerie/slots/${editingSlot._id}`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plage d\'ouverture mise à jour');
      } else {
        if (canAssignResponsable) {
          if (!responsableUserId) {
            toast.error('Veuillez sélectionner le responsable de cette plage d\'ouverture');
            return;
          }
        }
        const body = {
          ...payload,
          ...(canAssignResponsable ? { responsableUserId } : {}),
        };
        await axios.post(
          api('/miellerie/slots'),
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plage d\'ouverture créée');
      }

      setShowModal(false);
      resetForm();
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const deletePlageSansReservations = async (slotId) => {
    const token = localStorage.getItem('token');
    await axios.delete(api(`/miellerie/slots/${slotId}`), {
      headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Plage d\'ouverture retirée');
    fetchSlots();
  };

  /** Un seul bouton « Annuler » : sans résa → modale de confirmation puis DELETE ; avec résa → modale motif + e-mails (POST annuler). */
  const requestRetraitPlage = (slot) => {
    if (isSlotEndTimePast(slot)) {
      toast.error('Impossible d\'annuler une plage dont l\'heure de fin est passée.');
      return;
    }
    const n = Number(slot.reservationsActivesCount) || 0;
    if (n > 0) {
      setCancelPlageTarget(slot);
      setAnnulationMotif('');
      return;
    }
    setConfirmRetraitPlageSlot(slot);
  };

  const closeConfirmRetraitPlageModal = () => {
    if (retraitSansResaSubmitting) return;
    setConfirmRetraitPlageSlot(null);
  };

  const confirmRetraitPlageSansReservations = async () => {
    if (!confirmRetraitPlageSlot) return;
    setRetraitSansResaSubmitting(true);
    try {
      await deletePlageSansReservations(confirmRetraitPlageSlot._id);
      setConfirmRetraitPlageSlot(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du retrait de la plage');
    } finally {
      setRetraitSansResaSubmitting(false);
    }
  };

  const closeCancelPlageModal = () => {
    setCancelPlageTarget(null);
    setAnnulationMotif('');
  };

  const confirmAnnulationAvecReservations = async (e) => {
    e.preventDefault();
    const r = annulationMotif.trim();
    if (!r) {
      toast.error('Indiquez un motif d\'annulation (il sera communiqué aux usagers par e-mail).');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        api(`/miellerie/slots/${cancelPlageTarget._id}/annuler`),
        { raison: r },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeCancelPlageModal();
      toast.success('Ouverture annulée. Les usagers concernés ont été notifiés par e-mail.');
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const openEditModal = (slot) => {
    if (isSlotEndTimePast(slot)) {
      toast.error('Impossible de modifier une plage dont l\'heure de fin est passée.');
      return;
    }
    setEditingSlot(slot);
    setResponsableUserId('');
    const deb = parseHeureQuarts(debutPlage(slot));
    const fin = parseHeureQuarts(finPlage(slot));
    setFormData({
      date: jourISO(slot),
      heureDebut: joinHeure(deb.h, deb.m),
      heureFin: joinHeure(fin.h, fin.m),
      notes: slot.notes || '',
      servicesOuverts:
        Array.isArray(prestationsProposeesPlage(slot)) &&
        prestationsProposeesPlage(slot).length > 0
          ? prestationsSansIndisponibles([
              ...new Set(
                prestationsProposeesPlage(slot).map((v) =>
                  v === 'extraction' ? 'extraction_hausses' : v
                )
              ),
            ])
          : [...ALL_SERVICE_VALUES],
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      date: '',
      heureDebut: '09:00',
      heureFin: '12:00',
      notes: '',
      servicesOuverts: [...ALL_SERVICE_VALUES],
    });
    setEditingSlot(null);
    setResponsableUserId('');
    setListeResponsables([]);
  };

  const openGestionReservation = (r) => {
    setGestionModalError('');
    setRaisonAnnulationGestion('');
    setGestionModalStep(1);
    setGestionReservation(r);
    setShowCautionFromGestion(false);
    setCautionError('');
  };

  const closeGestionReservation = () => {
    setGestionReservation(null);
    setRaisonAnnulationGestion('');
    setGestionModalError('');
    setGestionModalStep(1);
    setStep3Quantite('');
    setStep3Horaire('');
    setStep3Notes('');
    setStep3ModePaiement('espece');
    setStep3NotePaiement('');
    setStep3RefCheque('');
    setPreviewMontantTotal(null);
    setPreviewMontantLoading(false);
    setPreviewMontantPrepaye(0);
    setPreviewDejaPayeEnLigne(false);
    setPreviewEcart(null);
    setShowCautionFromGestion(false);
    setCautionForm({ dateReception: '', note: '', document: null });
    setCautionError('');
  };

  const openCautionFromGestion = () => {
    setCautionForm({
      dateReception: new Date().toISOString().slice(0, 10),
      note: '',
      document: null,
    });
    setCautionError('');
    setShowCautionFromGestion(true);
  };

  const closeCautionFromGestion = () => {
    if (cautionSubmitting) return;
    setShowCautionFromGestion(false);
    setCautionForm({ dateReception: '', note: '', document: null });
    setCautionError('');
  };

  const handleMarkCautionRecu = async () => {
    const serviceId = gestionReservation?.serviceId;
    if (!serviceId) return;
    setCautionError('');
    setCautionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('status', 'recu');
      if (cautionForm.dateReception) fd.append('dateReception', cautionForm.dateReception);
      if (cautionForm.note?.trim()) fd.append('note', cautionForm.note.trim());
      if (cautionForm.document) fd.append('documentCaution', cautionForm.document);
      await axios.put(api(`/services/${serviceId}/caution`), fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setGestionReservation((prev) =>
        prev ? { ...prev, cautionStatus: 'recu' } : prev
      );
      setShowCautionFromGestion(false);
      setCautionForm({ dateReception: '', note: '', document: null });
      toast.success('Caution marquée comme reçue');
      refreshDayModal();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erreur lors de la mise à jour de la caution';
      setCautionError(msg);
      toast.error(msg);
    } finally {
      setCautionSubmitting(false);
    }
  };

  const goToGestionStep3 = () => {
    const r = gestionReservation;
    const t = (r?.prestations || r?.taches || [])[0];
    setStep3Quantite(String(t?.quantitePrevue ?? '0'));
    setStep3Horaire(r?.horaireArriveePrevue || '');
    setStep3Notes('');
    setStep3ModePaiement('espece');
    setStep3NotePaiement('');
    setStep3RefCheque('');
    setGestionModalError('');
    setGestionModalStep(3);
  };

  const refreshDayModal = useCallback(async () => {
    if (!dayResasModal) return;
    const { iso, originalSlots } = dayResasModal;
    await openDayResasModal(iso, originalSlots || [], true);
    fetchSlots();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayResasModal]);

  const confirmerAbsenceAdherent = async () => {
    const id = gestionReservation?._id;
    if (!id) return;
    setGestionModalError('');
    setGestionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        api(`/miellerie/reservations/${id}/absence-adherent`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeGestionReservation();
      toast.success('Absence enregistrée');
      refreshDayModal();
    } catch (err) {
      setGestionModalError(err.response?.data?.message || err.message || 'Enregistrement impossible.');
    } finally {
      setGestionSubmitting(false);
    }
  };

  const confirmerAnnulationGestion = async () => {
    const id = gestionReservation?._id;
    if (!id) return;
    const motif = raisonAnnulationGestion.trim();
    if (!motif) {
      setGestionModalError("Veuillez indiquer un motif d'annulation.");
      return;
    }
    setGestionModalError('');
    setGestionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        api(`/miellerie/reservations/${id}/annuler-gestion`),
        { raison: motif },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeGestionReservation();
      toast.success('Réservation annulée');
      refreshDayModal();
    } catch (err) {
      setGestionModalError(err.response?.data?.message || err.message || 'Annulation impossible.');
    } finally {
      setGestionSubmitting(false);
    }
  };

  const confirmerRealisationPrestation = async () => {
    const id = gestionReservation?._id;
    const t0 = (gestionReservation?.prestations || gestionReservation?.taches || [])[0];
    if (!id || !t0) { setGestionModalError('Données réservation invalides.'); return; }
    const q = Math.floor(Number(step3Quantite));
    if (Number.isNaN(q) || q < 0) { setGestionModalError('Indiquez une quantité effective valide.'); return; }
    if (!step3Horaire || !String(step3Horaire).trim()) { setGestionModalError("Indiquez l'horaire d'arrivée effective."); return; }

    const dejaPayeEnLigne =
      gestionReservation?.paiement?.statut === 'paye' &&
      ['stripe', 'solde'].includes(gestionReservation?.paiement?.methode) &&
      Number(gestionReservation?.montantPrepaye) > 0;
    const montantPrepaye = Number(gestionReservation?.montantPrepaye) || 0;
    const montantDu = previewMontantTotal != null ? Number(previewMontantTotal) : null;
    const ecart =
      dejaPayeEnLigne && montantDu != null
        ? Math.round((montantDu - montantPrepaye) * 100) / 100
        : null;

    const besoinReglementClassique =
      prestationGenereTarif(t0) && !dejaPayeEnLigne;
    const besoinComplement =
      dejaPayeEnLigne && ecart != null && ecart > 0;

    if (
      (besoinReglementClassique || besoinComplement) &&
      !['espece', 'cheque', 'dette'].includes(step3ModePaiement)
    ) {
      setGestionModalError(
        besoinComplement
          ? 'Choisissez un mode de règlement pour le complément dû.'
          : 'Choisissez un mode de règlement.'
      );
      return;
    }
    setGestionModalError('');
    setGestionSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        prestations: [{ type: t0.type, quantiteEffective: q }],
        horaireArriveePrevue: String(step3Horaire).trim(),
      };
      if (step3Notes.trim()) body.notes = step3Notes.trim();
      if (besoinReglementClassique || besoinComplement) {
        body.paiement =
          step3ModePaiement === 'dette'
            ? { statut: 'dette' }
            : {
                statut: 'paye',
                methode: step3ModePaiement,
                note: step3NotePaiement.trim() || undefined,
                reference: step3RefCheque.trim() || undefined,
              };
      }
      await axios.post(api(`/miellerie/reservations/${id}/confirmer`), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      closeGestionReservation();
      toast.success('Prestation enregistrée');
      refreshDayModal();
    } catch (err) {
      setGestionModalError(err.response?.data?.message || err.message || 'Enregistrement impossible.');
    } finally {
      setGestionSubmitting(false);
    }
  };

  const openDayResasModal = async (iso, daySlots, forceAll = false) => {
    const label = new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    setDayResasModal({ iso, label, loading: true, data: [], originalSlots: daySlots });
    try {
      const token = localStorage.getItem('token');
      /** Tous les créneaux du jour, même sans réservation */
      const slotsToFetch = forceAll
        ? [...daySlots]
        : daySlots.filter((s) => (s.reservationsActivesCount || 0) > 0);

      const details = await Promise.all(
        slotsToFetch.map(async (slot) => {
          const hasResas =
            (slot.reservationsTotalCount || slot.reservationsActivesCount || 0) > 0;
          if (!hasResas) {
            return { slot, reservations: [] };
          }
          const { data } = await axios.get(api(`/miellerie/slots/${slot._id}`), {
            headers: { Authorization: `Bearer ${token}` },
          });
          return { slot, reservations: data.reservations || [] };
        })
      );
      setDayResasModal((prev) =>
        prev?.iso === iso
          ? { iso, label, loading: false, data: details, originalSlots: daySlots }
          : prev
      );
    } catch {
      toast.error('Erreur lors du chargement des réservations');
      setDayResasModal((prev) =>
        prev?.iso === iso
          ? { iso, label, loading: false, data: [], originalSlots: daySlots }
          : prev
      );
    }
  };

  /** Clic sur une case du calendrier mois : affiche ce jour dans la colonne latérale */
  const selectMonthDay = (iso, daySlots) => {
    if (selectedMonthDayIso === iso) return;
    setSelectedMonthDayIso(iso);
    openDayResasModal(iso, daySlots, true);
  };

  const openAddModalForDate = (dayDate) => {
    if (!dayDate) return;
    if (isDateBeforeToday(dayDate)) {
      toast.error('Impossible d\'ajouter une plage sur une date passée.');
      return;
    }
    setEditingSlot(null);
    setResponsableUserId('');
    setListeResponsables([]);
    setFormData({
      date: toISODateLocal(dayDate),
      heureDebut: '09:00',
      heureFin: '12:00',
      notes: '',
      servicesOuverts: [...ALL_SERVICE_VALUES],
    });
    setShowModal(true);
  };

  const slotsByISODate = useMemo(() => {
    const m = new Map();
    for (const slot of slots) {
      const k = jourISO(slot);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(slot);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)));
    }
    return m;
  }, [slots]);

  const goPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear((y) => y - 1);
    } else {
      setSelectedMonth((mo) => mo - 1);
    }
  };

  const goNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear((y) => y + 1);
    } else {
      setSelectedMonth((mo) => mo + 1);
    }
  };

  const goToToday = () => {
    const p = partsHeureReunion();
    setSelectedYear(p.year);
    setSelectedMonth(p.month - 1);
    setSelectedMonthDayIso(todayISOReunion());
  };

  const goAdjacentDay = (delta) => {
    const iso = selectedMonthDayIso || todayISOReunion();
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedYear(d.getFullYear());
    setSelectedMonth(d.getMonth());
    setSelectedMonthDayIso(toISODateLocal(d));
  };

  const openDayPicker = () => {
    setPickerMonth(selectedMonth);
    setPickerYear(selectedYear);
    setShowDayPicker(true);
  };

  const goPrevPickerMonth = () => {
    if (pickerMonth === 0) {
      setPickerMonth(11);
      setPickerYear((y) => y - 1);
    } else {
      setPickerMonth((mo) => mo - 1);
    }
  };

  const goNextPickerMonth = () => {
    if (pickerMonth === 11) {
      setPickerMonth(0);
      setPickerYear((y) => y + 1);
    } else {
      setPickerMonth((mo) => mo + 1);
    }
  };

  const pickerMonthGrid = useMemo(
    () => getMonthCalendarCells(pickerYear, pickerMonth),
    [pickerYear, pickerMonth]
  );

  const pickerSlotsByISODate = useMemo(() => {
    const map = new Map();
    for (const slot of pickerSlots) {
      const iso = jourISO(slot);
      if (!iso) continue;
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(slot);
    }
    return map;
  }, [pickerSlots]);

  useEffect(() => {
    if (!showDayPicker) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          api(`/miellerie/slots?annee=${pickerYear}&mois=${pickerMonth + 1}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setPickerSlots(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPickerSlots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDayPicker, pickerMonth, pickerYear]);

  const goToFirstPendingPast = () => {
    const iso = pendingPastResas.firstIso;
    if (!iso) return;
    const [y, m] = iso.split('-').map(Number);
    const month0 = m - 1;
    const sameMonth = y === selectedYear && month0 === selectedMonth;
    setSelectedYear(y);
    setSelectedMonth(month0);
    setSelectedMonthDayIso(iso);
    setPlanningView('jour');
    // Même mois : les slots sont déjà chargés → ouvrir tout de suite
    if (sameMonth) {
      const daySlots = slotsByISODate.get(iso) || [];
      openDayResasModal(iso, daySlots, true);
    }
    // Autre mois : le useEffect rouvrira le panneau dès que les slots du mois seront chargés
  };

  const displayedSlotsCount = slots.length;
  const monthGrid = useMemo(
    () => getMonthCalendarCells(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  /** Panneau toujours ouvert — aujourd’hui si le mois affiché le contient, sinon le 1er */
  useEffect(() => {
    if (loading) return;

    // Pendant le chargement d’un autre mois, ignorer les slots encore « stale »
    const slotsMatchMonth =
      slots.length === 0 ||
      slots.some((s) => {
        const iso = jourISO(s);
        if (!iso) return false;
        const [y, m] = iso.split('-').map(Number);
        return y === selectedYear && m === selectedMonth + 1;
      });
    if (slots.length > 0 && !slotsMatchMonth) return;

    const todayIso = todayISOReunion();
    const [ty, tm] = todayIso.split('-').map(Number);
    const monthContainsToday =
      selectedYear === ty && selectedMonth === tm - 1;

    let targetIso = selectedMonthDayIso;
    if (targetIso) {
      const [y, m] = targetIso.split('-').map(Number);
      if (y !== selectedYear || m !== selectedMonth + 1) {
        targetIso = null;
      }
    }
    if (!targetIso) {
      targetIso = monthContainsToday
        ? todayIso
        : toISODateLocal(new Date(selectedYear, selectedMonth, 1));
    }

    const daySlots = slotsByISODate.get(targetIso) || [];

    // Déjà ouvert sur ce jour : ne pas recharger sauf si les créneaux ont changé
    if (
      dayResasModal?.iso === targetIso &&
      selectedMonthDayIso === targetIso &&
      !dayResasModal.loading
    ) {
      const prevIds = (dayResasModal.originalSlots || [])
        .map((s) => String(s._id))
        .sort()
        .join(',');
      const nextIds = daySlots
        .map((s) => String(s._id))
        .sort()
        .join(',');
      if (prevIds === nextIds) return;
    }

    setSelectedMonthDayIso(targetIso);
    openDayResasModal(targetIso, daySlots, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedMonthDayIso, loading, slotsByISODate]);

  const renderDayReservationsList = () => {
    if (!dayResasModal) return null;

    const canAddCreneau =
      canManageActivite &&
      Boolean(dayResasModal.iso) &&
      !isDateBeforeToday(new Date(`${dayResasModal.iso}T12:00:00`));

    const addCreneauButton = canAddCreneau ? (
      <button
        type="button"
        onClick={() => openAddModalForDate(new Date(`${dayResasModal.iso}T12:00:00`))}
        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-honey-800 bg-white border border-honey-200 rounded-xl hover:bg-honey-50 shadow-sm transition-colors"
      >
        <PlusIcon className="h-4 w-4" />
        Ajouter un créneau
      </button>
    ) : null;

    if (dayResasModal.loading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-honey-600" />
        </div>
      );
    }
    if (dayResasModal.data.length === 0) {
      return (
        <div className="space-y-2.5">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-8">
            <p className="text-sm text-slate-400 text-center">Aucun créneau ce jour</p>
          </div>
          {addCreneauButton}
        </div>
      );
    }

    return (
      <div className="space-y-2.5">
        {dayResasModal.data.map(({ slot, reservations }) => {
          const plagePassee = isSlotEndTimePast(slot);
          return (
          <div
            key={slot._id}
            className="space-y-2 rounded-2xl bg-white shadow-sm border border-slate-200 px-3.5 py-3"
          >
            <div className="flex items-start gap-2 px-1">
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-black tabular-nums">
                  {debutPlage(slot)} – {finPlage(slot)}
                </span>
                {slot.responsable && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    Responsable : {slot.responsable.prenom} {slot.responsable.nom}
                  </p>
                )}
              </div>
              {!plagePassee && canManageActivite && (
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEditModal(slot)}
                    className="p-1 text-blue-700 hover:bg-blue-50 rounded"
                    title="Modifier"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => requestRetraitPlage(slot)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            {reservations.length === 0 ? (
              <div className="rounded-xl bg-slate-50 border border-dashed border-slate-200 px-3.5 py-3">
                <p className="text-sm text-slate-500 text-center">Aucune réservation</p>
              </div>
            ) : (
            <div className="space-y-2">
              {reservations.map((r) => {
                const statutNorm = normaliseStatutResa(r.statut);
                const reservee = statutNorm === 'reservee';
                const peutEtreGeree = reservationPeutEtreGeree(r);
                const peutVoir = !peutEtreGeree && reservationEstTerminee(r);
                const prestations = lignesPrestationReservation(r);
                return (
                  <div
                    key={r._id}
                    className={`flex items-start gap-3 w-full rounded-2xl bg-white shadow-sm border px-3.5 py-3 ${
                      !reservee ? 'border-slate-100 opacity-80' : 'border-slate-100'
                    }`}
                  >
                    <div className="shrink-0 flex flex-col items-center justify-center rounded-xl w-12 h-12 bg-honey-50 text-honey-700">
                      <span className="text-sm font-bold tabular-nums leading-none">
                        {(r.horaireArriveePrevue || '—').split(':')[0]}
                      </span>
                      <span className="text-[10px] font-semibold tabular-nums mt-0.5">
                        {(r.horaireArriveePrevue || '').includes(':')
                          ? r.horaireArriveePrevue.split(':')[1]
                          : ''}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 truncate text-sm">
                        {r.usager
                          ? `${r.usager.prenom || ''} ${r.usager.nom || ''}`.trim()
                          : '—'}
                      </p>
                      {prestations.length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {prestations.map((p, pi) => {
                            const tsLabel = labelPrestationType(p.type);
                            return (
                              <span
                                key={pi}
                                className="inline-flex items-center text-slate-500 text-xs font-medium"
                              >
                                {tsLabel}
                                {p.quantitePrevue != null ? ` (${p.quantitePrevue})` : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* {(r.prestations || r.taches || [])[0] && (
                        <p className="mt-0.5 text-xs text-slate-500 truncate">
                          {libelleOrigineRucher((r.prestations || r.taches || [])[0])}
                        </p>
                      )} */}
                      {r.cautionStatus === 'en_attente' && (
                        <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-honey-800 bg-honey-100 border border-honey-200 px-1.5 py-0.5 rounded-full">
                          ⚠️ Caution en attente
                        </span>
                      )}
                      {statutNorm !== 'reservee' ? (
                        <PastillesReservation reservation={r} />
                      ) : null}
                    </div>
                    <div className="shrink-0 flex items-stretch gap-2 self-center">
                      {peutEtreGeree && canManageActivite && (
                        <button
                          type="button"
                          onClick={() => openGestionReservation(r)}
                          className="text-xs text-honey-700 hover:text-honey-900 bg-honey-100 hover:bg-honey-200 px-2.5 py-1 rounded-md font-semibold"
                        >
                          Gérer
                        </button>
                      )}
                      {peutVoir && (
                        <button
                          type="button"
                          onClick={() => openGestionReservation(r)}
                          className="text-xs text-slate-700 hover:text-slate-900 bg-slate-200 hover:bg-slate-300 px-2.5 py-1 rounded-md font-semibold"
                        >
                          Voir
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })}
        {addCreneauButton}
      </div>
    );
  };

  const renderAdminReservationCardCompact = (r) => {
    const statutNorm = normaliseStatutResa(r.statut);
    const reservee = statutNorm === 'reservee';
    const peutEtreGeree = reservationPeutEtreGeree(r);
    const peutVoir = !peutEtreGeree && reservationEstTerminee(r);
    const prestations = lignesPrestationReservation(r);
    const hh = (r.horaireArriveePrevue || '—').split(':')[0];
    const mm = (r.horaireArriveePrevue || '').includes(':')
      ? r.horaireArriveePrevue.split(':')[1]
      : '';
    return (
      <div
        key={r._id}
        className={`flex items-center justify-between gap-2 w-full rounded-lg bg-white shadow-sm border pr-1.5 ${
          !reservee ? 'border-slate-100 opacity-80' : 'border-slate-100'
        }`}
      >
        <div className="flex items-center gap-2">
        <div className="text-xs font-bold shrink-0 flex items-center justify-center rounded-md bg-honey-50 text-honey-700 px-2 sm:px-3 py-2">
          <span className="tabular-nums leading-none">{hh}</span>
          <span className="tabular-nums">:</span>
          {mm ? <span className="tabular-nums">{mm}</span> : null}
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <p className="font-bold text-slate-900 truncate text-sm">
            {r.usager ? `${r.usager.prenom || ''} ${r.usager.nom || ''}`.trim() : '—'}
          </p>
          <span className="text-sm text-slate-500">·</span>
          {prestations.length > 0 && (
            <p className="text-xs text-slate-500 truncate">
              {prestations
                .map((p) => {
                  return `${labelPrestationType(p.type)}${p.quantitePrevue != null ? ` (${p.quantitePrevue})` : ''}`;
                })
                .join(', ')}
            </p>
          )}
          {r.cautionStatus === 'en_attente' && (
            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-honey-800">
              ⚠️ Caution
            </span>
          )}
        </div>
        </div>
        <div className="shrink-0 flex items-stretch gap-2">
          {statutNorm !== 'reservee' ? (
            <PastillesReservation reservation={r} />
          ) : null}
          {peutEtreGeree && canManageActivite && (
            <button
              type="button"
              onClick={() => openGestionReservation(r)}
              className="text-xs text-honey-700 hover:text-honey-900 bg-honey-100 hover:bg-honey-200 px-2 py-1 rounded-md font-semibold"
            >
              Gérer
            </button>
          )}
          {peutVoir && (
            <button
              type="button"
              onClick={() => openGestionReservation(r)}
              className="text-xs text-slate-700 hover:text-slate-900 bg-slate-200 hover:bg-slate-300 px-2 py-1 rounded-md font-semibold"
            >
              Voir
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    if (!selectedMonthDayIso) return null;
    const selectedDayDate = new Date(`${selectedMonthDayIso}T12:00:00`);
    const selectedDayLabel =
      dayResasModal?.iso === selectedMonthDayIso && dayResasModal.label
        ? dayResasModal.label
        : selectedDayDate.toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          });
    const daySlots = slotsByISODate.get(selectedMonthDayIso) || [];
    const sortedDaySlots = [...daySlots].sort((a, b) =>
      debutPlage(a).localeCompare(debutPlage(b))
    );
    const detailsBySlotId = new Map(
      (dayResasModal?.iso === selectedMonthDayIso ? dayResasModal.data : []).map((d) => [
        String(d.slot._id),
        d.reservations || [],
      ])
    );
    const dayLoading =
      !dayResasModal ||
      dayResasModal.iso !== selectedMonthDayIso ||
      dayResasModal.loading;
    const canAddCreneau = canManageActivite && !isDateBeforeToday(selectedDayDate);
    const { startHour, endHour } = hourRangePourPlages(sortedDaySlots);
    const hours = [];
    for (let h = startHour; h <= endHour; h++) hours.push(h);
    const timelineHeight = Math.max(1, endHour - startHour) * TIMELINE_PX_PER_HOUR;
    const nowP = partsHeureReunion();
    const isToday = selectedMonthDayIso === todayISOReunion();
    const nowTop =
      ((nowP.hour * 60 + nowP.minute) / 60 - startHour) * TIMELINE_PX_PER_HOUR;
    const showNowLine = isToday && nowTop >= 0 && nowTop <= timelineHeight;

    return (
      <div className="rounded-2xl bg-slate-50/80 p-3 sm:p-5 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-honey-50 text-honey-700 shrink-0">
                <span className="text-2xl font-bold leading-none tabular-nums">
                  {parseInt(selectedMonthDayIso.split('-')[2], 10)}
                </span>
                <span className="text-[12px] font-semibold uppercase tracking-wide mt-0.5">
                  {selectedDayDate.toLocaleDateString('fr-FR', { weekday: 'short' })}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-slate-900 capitalize leading-snug">
                  {selectedDayLabel}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {sortedDaySlots.length} créneau
                  {sortedDaySlots.length !== 1 ? 'x' : ''} d&apos;ouverture
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goAdjacentDay(-1)}
                  className="p-2 sm:p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                  aria-label="Jour précédent"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goToToday}
                  className="px-2.5 py-1 text-xs font-semibold text-honey-700 bg-honey-50 border border-honey-200 rounded-lg hover:bg-honey-100 transition-colors"
                >
                  Aujourd&apos;hui
                </button>
                <button
                  type="button"
                  onClick={() => goAdjacentDay(1)}
                  className="p-2 sm:p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                  aria-label="Jour suivant"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
              <button
                type="button"
                onClick={openDayPicker}
                className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 shadow-sm transition-colors"
              >
                <CalendarDaysIcon className="h-4 w-4" />
                <span className="sm:hidden">Autre jour</span>
                <span className="hidden sm:inline">Choisir un autre jour</span>
              </button>
              {canAddCreneau && (
                <button
                  type="button"
                  onClick={() => openAddModalForDate(selectedDayDate)}
                  className="inline-flex self-start text-sm items-center px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors whitespace-nowrap"
                  >
                  <PlusIcon className="h-4 w-4 shrink-0" />
                  Ajouter une plage d'ouverture
                </button>
                
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="relative" style={{ height: timelineHeight + 16 }}>
            {hours.map((h, i) => (
              <div
                key={h}
                className="absolute left-0 right-0 flex items-start pointer-events-none"
                style={{ top: i * TIMELINE_PX_PER_HOUR + 8 }}
              >
                <span className="w-10 sm:w-14 shrink-0 -mt-2 pr-1.5 sm:pr-2 text-right text-[10px] sm:text-[11px] font-semibold text-slate-400 tabular-nums">
                  {String(h).padStart(2, '0')}:00
                </span>
                <div className="flex-1 border-t border-slate-100" />
              </div>
            ))}

            {showNowLine && (
              <div
                className="absolute left-10 sm:left-14 right-0 z-20 flex items-center pointer-events-none"
                style={{ top: nowTop + 8 }}
              >
                <span className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                <span className="text-[10px] font-bold text-red-600 bg-white/90 px-1 rounded mr-1 tabular-nums">
                  {formatHeureReunion()}
                </span>
                <div className="flex-1 border-t-2 border-red-400" />
              </div>
            )}

            {sortedDaySlots.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center pl-10 sm:pl-14">
                <div className="text-center px-6">
                  <ClockIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" aria-hidden />
                  <p className="text-sm text-slate-500">Pas d&apos;ouverture ce jour</p>
                </div>
              </div>
            ) : (
              sortedDaySlots.map((slot) => {
                const bounds = plageBoundsMinutes(slot);
                const startM = bounds.startM ?? startHour * 60;
                const endM = bounds.endM ?? endHour * 60;
                const top = (startM / 60 - startHour) * TIMELINE_PX_PER_HOUR + 8;
                const height = Math.max(
                  148,
                  ((endM - startM) / 60) * TIMELINE_PX_PER_HOUR - 6
                );
                const reservations = [...(detailsBySlotId.get(String(slot._id)) || [])].sort(
                  (a, b) =>
                    String(a.horaireArriveePrevue || '').localeCompare(
                      String(b.horaireArriveePrevue || '')
                    )
                );
                const resp = slot.responsable;
                const plagePassee = isSlotEndTimePast(slot);
                return (
                  <div
                    key={slot._id}
                    className="absolute left-11 sm:left-16 right-2 sm:right-3 z-10 flex flex-row rounded-2xl bg-honey-50/70 border border-honey-200/80 shadow-sm overflow-hidden"
                    style={{ top, height }}
                  >
                    <div className="w-[7.25rem] sm:w-52 shrink-0 flex flex-col justify-between gap-1.5 sm:gap-2 px-2 sm:px-3.5 py-2 sm:py-3 border-r border-honey-200/70">
                      <div className="min-w-0">
                        <span className="font-semibold text-slate-900 tabular-nums text-sm sm:text-base">
                          {debutPlage(slot)} – {finPlage(slot)}
                        </span>
                        {resp && (resp.prenom || resp.nom) && (
                          <p className="text-sm sm:text-xs text-slate-500 mt-1 leading-snug">
                            <span className="hidden sm:inline">Responsable : </span>
                            {[resp.prenom, resp.nom].filter(Boolean).join(' ')}
                          </p>
                        )}
                        {slot.statut && slot.statut !== 'ouvert' && (
                          <p className="text-[11px] text-slate-500 mt-1 capitalize">{slot.statut}</p>
                        )}
                        {slot.notes?.trim() && (
                          <p className="text-[11px] sm:text-xs text-slate-500 italic mt-1 line-clamp-2">
                            {slot.notes}
                          </p>
                        )}
                      </div>
                      {!plagePassee && canManageActivite && (
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(slot)}
                            className="p-1 text-blue-700 hover:bg-blue-50 rounded"
                            title="Modifier"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => requestRetraitPlage(slot)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto px-2.5 py-2.5 space-y-2">
                      {dayLoading ? (
                        <div className="h-full min-h-[4.5rem] flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-honey-600" />
                        </div>
                      ) : reservations.length === 0 ? (
                        <div className="h-full min-h-[4.5rem] flex items-center justify-center rounded-xl bg-white/70 border border-dashed border-honey-200 px-3.5 py-3">
                          <p className="text-sm text-slate-500 text-center">Aucune réservation</p>
                        </div>
                      ) : (
                        reservations.map(renderAdminReservationCardCompact)
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {canAddCreneau ? (
          <button
            type="button"
            onClick={() => openAddModalForDate(selectedDayDate)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-honey-800 bg-white border border-honey-200 rounded-xl hover:bg-honey-50 shadow-sm transition-colors"
          >
            <PlusIcon className="h-4 w-4 shrink-0" />
            Ajouter un créneau
          </button>
        ) : canManageActivite ? (
          <p className="text-sm text-slate-500 text-center py-2.5 px-1 leading-snug bg-white rounded-xl border border-slate-100">
            Jour passé — ajout de plage impossible.
          </p>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-8">
        <p className="text-slate-600 mt-1">
        Indiquez quand vous êtes présent à la miellerie pour accueillir les usagers et quelles prestations sont disponibles sur chaque plage. Quotas pour les usagers : 1 réservation par jour (dépôt, extraction, conditionnement, gaufrage) ; 3 fabrications de candi par créneau.
        </p>
        {canManageActivite && (
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="inline-flex self-start text-sm items-center px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span className="sm:hidden">Ajouter une plage</span>
          <span className="hidden sm:inline">Ajouter une plage d&apos;ouverture</span>
        </button>
        )}
      </div>

      {/* Messages (masqués si la modale est ouverte : l’erreur s’affiche dans la popup) */}

      {canManageActivite && pendingPastResas.count > 0 && (
        <button
          type="button"
          onClick={goToFirstPendingPast}
          className="w-full text-left rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3 hover:bg-red-100/80 transition-colors"
        >
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-red-900">
              Réservations passées non gérées
            </p>
            <p className="text-sm text-red-800 mt-0.5">
              Merci de régulariser les réservations des jours passés encore en attente
              ({pendingPastResas.count} réservation{pendingPastResas.count > 1 ? 's' : ''}).
              Cliquez pour ouvrir le premier jour concerné.
            </p>
          </div>
        </button>
      )}

      {/* Vues planning */}
      <div className="mt-6 pt-6 border-t border-slate-200 -mx-4 sm:-mx-6">
        <div className="px-4 sm:px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:flex-wrap border-b border-slate-100">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-0.5 shadow-sm self-start">
            <button
              type="button"
              onClick={() => setPlanningView('jour')}
              className={`px-3.5 py-1.5 text-sm font-semibold rounded-[10px] transition-colors ${
                planningView === 'jour'
                  ? 'bg-honey-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Jour
            </button>
            <button
              type="button"
              onClick={() => setPlanningView('calendrier')}
              className={`px-3.5 py-1.5 text-sm font-semibold rounded-[10px] transition-colors ${
                planningView === 'calendrier'
                  ? 'bg-honey-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Calendrier
            </button>
          </div>
          {planningView === 'calendrier' && (
            <div className="flex items-center justify-between gap-2 flex-1 flex-wrap">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="text-sm px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500"
                aria-label="Année"
              >
                {(() => {
                  const cur = new Date().getFullYear();
                  const yMin = Math.min(selectedYear, cur - 1);
                  const yMax = Math.max(selectedYear, cur + 4);
                  const opts = [];
                  for (let y = yMin; y <= yMax; y++) opts.push(y);
                  return opts.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ));
                })()}
              </select>
              <p className="text-sm text-slate-500 sm:ml-auto sm:text-right">
                {displayedSlotsCount} plage{displayedSlotsCount !== 1 ? 's' : ''} enregistrée
                {displayedSlotsCount !== 1 ? 's' : ''} sur ce mois
              </p>
            </div>
          )}
        </div>

        {planningView === 'jour' && (
          <div className="px-3 sm:px-4">{renderDayView()}</div>
        )}

        {planningView === 'calendrier' && (
        <div className="px-3 sm:px-4">
            <div className="flex flex-col lg:flex-row gap-5 items-start rounded-2xl bg-slate-50/80 p-3 sm:p-4 -mx-1">
              {/* Calendrier mensuel — style carte épurée */}
              <div className="min-w-0 w-full lg:flex-1 bg-white rounded-2xl shadow-sm border border-slate-100/80 overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 sm:px-5 py-3 sm:py-4 border-b border-slate-50">
                  <h3 className="text-base font-bold text-slate-900 hidden sm:block">Calendrier</h3>
                  <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={goToToday}
                      className="px-2.5 py-1 text-xs font-semibold text-honey-700 bg-honey-50 border border-honey-200 rounded-lg hover:bg-honey-100 transition-colors"
                    >
                      Aujourd&apos;hui
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={goPrevMonth}
                        className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                        aria-label="Mois précédent"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                      <span className="text-sm font-semibold text-honey-700 capitalize min-w-0 sm:min-w-[9.5rem] text-center">
                        {new Date(selectedYear, selectedMonth, 1).toLocaleDateString('fr-FR', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={goNextMonth}
                        className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                        aria-label="Mois suivant"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-7 px-3 sm:px-4 pt-3">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((label) => (
                    <div
                      key={label}
                      className="text-center text-[10px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider py-1.5 sm:py-2"
                    >
                      {label}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-y-1 px-2 sm:px-3 pb-4">
                  {monthGrid.map((dayDate, idx) => {
                    const iso = toISODateLocal(dayDate);
                    const inMonth = dayDate.getMonth() === selectedMonth;
                    const daySlots = inMonth ? slotsByISODate.get(iso) || [] : [];
                    const totalResasDuJour = daySlots.reduce(
                      (sum, s) => sum + (s.reservationsActivesCount || 0),
                      0
                    );
                    /** Résas non annulées (y compris honorées / absentes) — pour le badge des jours passés */
                    const totalResasNonAnnulees = daySlots.reduce((sum, s) => {
                      const sc = s.statusCounts || {};
                      return (
                        sum +
                        (sc.reservee || 0) +
                        (sc.honoree || 0) +
                        (sc.absente || 0)
                      );
                    }, 0);
                    const hasSlots = daySlots.length > 0;
                    const hasResas = totalResasDuJour > 0 || totalResasNonAnnulees > 0;
                    const isSelected = selectedMonthDayIso === iso;
                    const aValiderJour = jourADesReservationsAGerer(daySlots);
                    const countAGerer = countReservationsAGerer(daySlots);
                    const isPast = isISODateStrictlyPast(iso);
                    /** Passé + résas + tout géré */
                    const pastToutGere = isPast && hasResas && !aValiderJour;
                    /** Passé + résas + encore à gérer */
                    const pastAGerer = isPast && hasResas && aValiderJour;

                    return (
                      <button
                        key={`${iso}-${idx}`}
                        type="button"
                        onClick={() => {
                          if (!inMonth) {
                            setSelectedYear(dayDate.getFullYear());
                            setSelectedMonth(dayDate.getMonth());
                            setSelectedMonthDayIso(iso);
                            return;
                          }
                          selectMonthDay(iso, daySlots);
                        }}
                        className="relative flex flex-col items-center gap-0.5 sm:gap-1 py-1 sm:py-1.5 px-0 min-h-[2.75rem] sm:min-h-[5rem] rounded-lg sm:rounded-xl transition-colors hover:bg-slate-50/80"
                      >
                        <span className="relative inline-flex">
                          <span
                            className={`inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold tabular-nums transition-colors ${
                              !inMonth
                                ? 'text-slate-300'
                                : isSelected
                                  ? 'bg-honey-600 text-white shadow-sm'
                                  : pastAGerer
                                    ? 'text-slate-700 ring-1 ring-slate-400 bg-slate-50'
                                    : pastToutGere
                                      ? 'text-slate-400 bg-slate-100/80'
                                      : isPast && hasSlots
                                        ? 'text-slate-400'
                                        : hasResas
                                          ? 'text-honey-700 ring-1 ring-honey-400 bg-honey-50/40'
                                          : hasSlots
                                            ? 'text-slate-800 ring-1 ring-slate-200'
                                            : 'text-slate-700'
                            }`}
                          >
                            {dayDate.getDate()}
                          </span>
                          {inMonth && pastAGerer && (
                            <span
                              className="absolute -top-1 -right-1 z-[1] inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full text-[9px] font-bold text-white tabular-nums shadow-sm bg-red-500"
                              title={`${countAGerer} réservation${countAGerer > 1 ? 's' : ''} à gérer`}
                            >
                              {countAGerer}
                            </span>
                          )}
                          {inMonth && pastToutGere && (
                            <span
                              className={`absolute -top-1 -right-1 z-[1] inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full text-[9px] font-bold text-white tabular-nums shadow-sm ${
                                isSelected ? 'bg-honey-500' : 'bg-slate-400'
                              }`}
                              title={`${totalResasNonAnnulees} réservation${totalResasNonAnnulees > 1 ? 's' : ''}`}
                            >
                              {totalResasNonAnnulees}
                            </span>
                          )}
                          {inMonth && hasResas && !isPast && (
                            <span
                              className={`absolute -top-1 -right-1 z-[1] inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full text-[9px] font-bold text-white tabular-nums shadow-sm ${
                                aValiderJour ? 'bg-honey-700' : 'bg-honey-500'
                              }`}
                              title={`${totalResasDuJour || totalResasNonAnnulees} réservation${(totalResasDuJour || totalResasNonAnnulees) > 1 ? 's' : ''}`}
                            >
                              {totalResasDuJour || totalResasNonAnnulees}
                            </span>
                          )}
                        </span>

                        {inMonth && hasSlots && (
                          <>
                            <span
                              className={`sm:hidden w-1 h-1 rounded-full mt-0.5 ${
                                pastAGerer
                                  ? 'bg-red-500'
                                  : hasResas
                                    ? 'bg-honey-500'
                                    : 'bg-slate-300'
                              }`}
                            />
                            <div className="hidden sm:block w-full space-y-0.5 px-0.5 pt-1">
                              {daySlots.slice(0, 2).map((slot) => (
                                <div
                                  key={slot._id}
                                  className={`text-xs tabular-nums truncate text-center leading-tight ${
                                    isSelected
                                      ? 'text-honey-800/80'
                                      : pastToutGere || (isPast && !pastAGerer)
                                        ? 'text-slate-400'
                                        : pastAGerer
                                          ? 'text-slate-500'
                                          : slot.statut === 'ouvert'
                                            ? 'text-slate-500'
                                            : slot.statut === 'ferme'
                                              ? 'text-slate-400'
                                              : 'text-red-500'
                                  }`}
                                >
                                  {debutPlage(slot)} – {finPlage(slot)}
                                </div>
                              ))}
                              {daySlots.length > 2 && (
                                <div className="text-xs text-center text-slate-400">
                                  +{daySlots.length - 2}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Colonne latérale — détail du jour */}
              {selectedMonthDayIso && dayResasModal && (
                <aside className="w-full lg:w-[22rem] xl:w-96 shrink-0 lg:sticky lg:top-4 flex flex-col gap-3 max-h-none lg:max-h-[min(75vh,44rem)]">
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-honey-50 text-honey-700 shrink-0">
                        <span className="text-2xl font-bold leading-none tabular-nums">
                          {parseInt(selectedMonthDayIso.split('-')[2], 10)}
                        </span>
                        <span className="text-[12px] font-semibold uppercase tracking-wide mt-0.5">
                          {new Date(`${selectedMonthDayIso}T12:00:00`).toLocaleDateString('fr-FR', {
                            weekday: 'short',
                          })}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <h3 className="text-lg font-semibold text-slate-900 capitalize leading-snug">
                          {dayResasModal.label}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {(slotsByISODate.get(selectedMonthDayIso) || []).length} créneau
                          {(slotsByISODate.get(selectedMonthDayIso) || []).length !== 1 ? 'x' : ''}
                        </p>
                        <button
                          type="button"
                          onClick={() => setPlanningView('jour')}
                          className="mt-1.5 text-xs font-semibold text-honey-700 hover:text-honey-900"
                        >
                          Voir la journée
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-2.5 pr-0.5 min-h-0">
                    {renderDayReservationsList()}
                  </div>
                </aside>
              )}
            </div>
        </div>
        )}
      </div>

      {showDayPicker && (
        <Portal>
          <div
            className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40"
            onClick={() => setShowDayPicker(false)}
          >
            <div
              className="w-full sm:max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-100"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-labelledby="day-picker-title"
              aria-modal="true"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
                <h3 id="day-picker-title" className="text-base font-bold text-slate-900">
                  Choisir un jour
                </h3>
                <button
                  type="button"
                  onClick={() => setShowDayPicker(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center justify-between px-5 py-3">
                <button
                  type="button"
                  onClick={goPrevPickerMonth}
                  className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                  aria-label="Mois précédent"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <span className="text-sm font-semibold text-honey-700 capitalize min-w-[9.5rem] text-center">
                  {new Date(pickerYear, pickerMonth, 1).toLocaleDateString('fr-FR', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
                <button
                  type="button"
                  onClick={goNextPickerMonth}
                  className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                  aria-label="Mois suivant"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-7 px-3 sm:px-4">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((label) => (
                  <div
                    key={label}
                    className="text-center text-[11px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider py-2"
                  >
                    {label}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-1 px-2 sm:px-3 pb-4">
                {pickerMonthGrid.map((dayDate, idx) => {
                  const iso = toISODateLocal(dayDate);
                  const inMonth = dayDate.getMonth() === pickerMonth;
                  const daySlots = inMonth ? pickerSlotsByISODate.get(iso) || [] : [];
                  const totalResasDuJour = daySlots.reduce(
                    (sum, s) => sum + (s.reservationsActivesCount || 0),
                    0
                  );
                  const hasSlots = daySlots.length > 0;
                  const hasResas = totalResasDuJour > 0;
                  const isSelected = selectedMonthDayIso === iso;
                  const isPast = isISODateStrictlyPast(iso);

                  return (
                    <button
                      key={`${iso}-${idx}`}
                      type="button"
                      onClick={() => {
                        setSelectedYear(dayDate.getFullYear());
                        setSelectedMonth(dayDate.getMonth());
                        setSelectedMonthDayIso(iso);
                        setShowDayPicker(false);
                      }}
                      className="relative flex flex-col items-center gap-1 py-1.5 px-0.5 min-h-[3.5rem] rounded-xl transition-colors hover:bg-slate-50/80"
                    >
                      <span className="relative inline-flex">
                        <span
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-semibold tabular-nums transition-colors ${
                            !inMonth
                              ? 'text-slate-300'
                              : isSelected
                                ? 'bg-honey-600 text-white shadow-sm'
                                : isPast
                                  ? hasResas
                                    ? 'text-slate-500 ring-1 ring-honey-300 bg-slate-100/80'
                                    : hasSlots
                                      ? 'text-slate-400'
                                      : 'text-slate-400 bg-slate-100/60'
                                  : hasResas
                                    ? 'text-honey-700 ring-1 ring-honey-400 bg-honey-50/40'
                                    : hasSlots
                                      ? 'text-slate-800 ring-1 ring-slate-200'
                                      : 'text-slate-700'
                          }`}
                        >
                          {dayDate.getDate()}
                        </span>
                        {inMonth && hasResas && (
                          <span
                            className={`absolute -top-1 -right-1 z-[1] inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full text-[9px] font-bold text-white tabular-nums shadow-sm ${
                              isSelected ? 'bg-honey-500' : isPast ? 'bg-slate-400' : 'bg-honey-500'
                            }`}
                          >
                            {totalResasDuJour}
                          </span>
                        )}
                      </span>
                      {inMonth && hasSlots && (
                        <div className="w-1.5 h-1.5 rounded-full bg-honey-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Modale gestion réservation (absent / annuler / prestation réalisée) */}
      {gestionReservation && !showCautionFromGestion && (
        <Portal>
        <div
          className={OVERLAY_CLASS_DARK}
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget && !gestionSubmitting) closeGestionReservation(); }}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
            role="dialog"
            aria-labelledby="gestion-resa-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
              <div>
                <h3 id="gestion-resa-title" className="text-base font-semibold text-slate-900">
                  {reservationPeutEtreGeree(gestionReservation)
                    ? 'Gérer la réservation'
                    : 'Voir la réservation'}
                </h3>
                <div className="flex gap-1">
                  <p className="text-sm text-slate-500">
                    {gestionReservation.usager
                      ? `${gestionReservation.usager.prenom || ''} ${gestionReservation.usager.nom || ''}`.trim()
                      : '—'}
                  </p>
                  <span className="text-sm text-slate-500">·</span>
                  {lignePrestation(gestionReservation) !== '—' && (
                    <p className="text-sm font-medium text-honey-800">
                      {lignePrestation(gestionReservation)}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closeGestionReservation}
                disabled={gestionSubmitting}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {gestionModalError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                  {gestionModalError}
                </div>
              )}

              {/* ── Step 1 : aperçu + choix action ── */}
              {gestionModalStep === 1 && (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">Arrivée prévue</span>
                      <span className="font-semibold tabular-nums text-slate-800">
                        {gestionReservation.horaireArriveePrevue || '—'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">Prestation</span>
                      <span className="font-medium text-slate-800">{lignePrestation(gestionReservation)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">Montant</span>
                      <span className="font-semibold tabular-nums text-slate-800">
                        {Number(gestionReservation.montantTotal || 0).toFixed(2)} €
                      </span>
                    </div>
                    {(() => {
                      const dette = detailReglementDette(gestionReservation);
                      if (!dette) return null;
                      return (
                        <>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">Déjà payé</span>
                            <span className="font-semibold tabular-nums text-slate-800">
                              {dette.dejaPaye.toFixed(2)} €
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-500">Reste en dette</span>
                            <span className="font-semibold tabular-nums text-honey-950">
                              {dette.restant.toFixed(2)} €
                            </span>
                          </div>
                          {dette.note ? (
                            <p className="text-xs text-slate-600 leading-snug pt-0.5">
                              {dette.note}
                            </p>
                          ) : null}
                        </>
                      );
                    })()}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-500">Rucher</span>
                      <span className="font-medium text-slate-800 text-right">
                        {libelleOrigineRucher(
                          (gestionReservation.prestations || gestionReservation.taches || [])[0]
                        )}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-slate-500 shrink-0 pt-0.5">Statut</span>
                      <PastillesReservation reservation={gestionReservation} />
                    </div>
                  </div>
                  {/* ── Statut caution (consultation seule) ── */}
                  {gestionReservation.serviceId && (
                    gestionReservation.cautionStatus === 'en_attente' ? (
                      <div className="rounded-lg border border-red-400 bg-red-100 px-4 py-2.5 space-y-2">
                        <p className="text-sm font-semibold">
                          ⚠️ Caution en attente
                          <span className="ml-1.5 text-xs font-normal text-red-600">
                            ({gestionReservation.cautionMontant || 300} €)
                          </span>
                        </p>
                        <button
                          type="button"
                          onClick={openCautionFromGestion}
                          className="w-full px-3 py-2 text-sm font-semibold text-white bg-honey-600 hover:bg-honey-700 rounded-lg transition-colors"
                        >
                          Marquer comme reçue
                        </button>
                      </div>
                    ) : (gestionReservation.cautionStatus === 'recu' ||
                      gestionReservation.cautionStatus === 'rendu') ? (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 flex items-center gap-2">
                        <span className="text-green-600 font-bold">✓</span>
                        <p className="text-sm font-medium text-green-800">
                          {gestionReservation.cautionStatus === 'rendu'
                            ? 'Caution rendue'
                            : 'Caution reçue'}
                        </p>
                      </div>
                    ) : null
                  )}

                  <div className="flex flex-col gap-2 pt-1">
                    {reservationPeutEtreGeree(gestionReservation) ? (
                      <>
                    <button
                      type="button"
                      onClick={() => { setGestionModalError(''); setGestionModalStep(2); }}
                      className="w-full px-4 py-2.5 border-2 border-red-200 text-red-800 rounded-lg hover:bg-red-50 font-medium text-sm"
                    >
                      Annuler la réservation
                    </button>
                    <button
                      type="button"
                      onClick={() => { setGestionModalError(''); setGestionModalStep(4); }}
                      className="w-full px-4 py-2.5 border-2 border-honey-300 text-honey-950 rounded-lg hover:bg-honey-50 font-medium text-sm"
                    >
                      L&apos;adhérent ne s&apos;est pas présenté
                    </button>
                    <button
                      type="button"
                      onClick={goToGestionStep3}
                      className="w-full px-4 py-2.5 bg-honey-600 text-white rounded-lg hover:bg-honey-700 font-medium text-sm"
                    >
                      Prestation réalisée — règlement
                    </button>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-1">
                        Cette réservation a déjà été traitée — consultation seule.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* ── Step 2 : annuler ── */}
              {gestionModalStep === 2 && (
                <>
                  <button type="button" onClick={() => { setGestionModalError(''); setGestionModalStep(1); }}
                    className="text-sm text-honey-700 font-medium hover:underline">← Retour</button>
                  <div className="rounded-lg border border-honey-200 bg-honey-50 px-3 py-2 text-sm text-honey-950">
                    <p className="font-medium">Annulation côté gestion</p>
                    <p className="mt-1 text-honey-900">L&apos;adhérent reçoit un e-mail avec le motif indiqué.</p>
                  </div>
                  <div>
                    <label htmlFor="raison-annulation-gestion" className="block text-sm font-medium text-slate-700 mb-1">
                      Motif <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      id="raison-annulation-gestion"
                      value={raisonAnnulationGestion}
                      onChange={(e) => setRaisonAnnulationGestion(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 text-sm"
                      placeholder="Obligatoire — visible par l'adhérent"
                      disabled={gestionSubmitting}
                    />
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                    <button type="button" onClick={closeGestionReservation} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                      Fermer
                    </button>
                    <button type="button" onClick={confirmerAnnulationGestion} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium">
                      {gestionSubmitting ? 'Traitement…' : "Valider l'annulation"}
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 4 : absent ── */}
              {gestionModalStep === 4 && (
                <>
                  <button type="button" onClick={() => { setGestionModalError(''); setGestionModalStep(1); }}
                    className="text-sm text-honey-700 font-medium hover:underline">← Retour</button>
                  <div className="rounded-lg border border-honey-200 bg-honey-50 px-3 py-2 text-sm text-honey-950">
                    <p className="font-medium">Non-présentation</p>
                    <p className="mt-1 text-honey-900">
                      La réservation passera au statut « absent·e ». L&apos;adhérent reçoit un e-mail d&apos;information.
                      Aucun montant ni dette n&apos;est généré.
                    </p>
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                    <button type="button" onClick={closeGestionReservation} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                      Fermer
                    </button>
                    <button type="button" onClick={confirmerAbsenceAdherent} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:opacity-50 font-medium">
                      {gestionSubmitting ? 'Enregistrement…' : "Confirmer l'absence"}
                    </button>
                  </div>
                </>
              )}

              {/* ── Step 3 : prestation réalisée ── */}
              {gestionModalStep === 3 && (
                <>
                  <button type="button" onClick={() => { setGestionModalError(''); setGestionModalStep(1); }}
                    className="text-sm text-honey-700 font-medium hover:underline">← Retour</button>
                  <p className="text-sm text-slate-600">Indiquez les éléments constatés lors de la prestation.</p>

                  <div>
                    <label htmlFor="gc-step3-quantite" className="block text-sm font-medium text-slate-700 mb-1">
                      Quantité effective
                      {(() => {
                        const t0 = (gestionReservation.prestations || gestionReservation.taches || [])[0];
                        const qp = t0?.quantitePrevue;
                        return qp != null ? (
                          <span className="ml-2 text-sm font-normal text-slate-500">
                            (annoncée : <span className="font-semibold text-slate-700">{qp}</span>)
                          </span>
                        ) : null;
                      })()}
                    </label>
                    <input id="gc-step3-quantite" type="number" min={0} step={1}
                      value={step3Quantite}
                      onChange={(e) => setStep3Quantite(e.target.value === '' ? '' : String(Math.max(0, Math.floor(Number(e.target.value)))))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 text-sm"
                      disabled={gestionSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="gc-step3-horaire" className="block text-sm font-medium text-slate-700 mb-1">Horaire d&apos;arrivée effective</label>
                    <input id="gc-step3-horaire" type="time"
                      value={step3Horaire}
                      onChange={(e) => setStep3Horaire(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 text-sm max-w-[12rem]"
                      disabled={gestionSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="gc-step3-notes" className="block text-sm font-medium text-slate-700 mb-1">Note (optionnel)</label>
                    <textarea id="gc-step3-notes" value={step3Notes} onChange={(e) => setStep3Notes(e.target.value)}
                      rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 text-sm"
                      placeholder="Observations…" disabled={gestionSubmitting}
                    />
                  </div>

                  {prestationGenereTarif((gestionReservation.prestations || gestionReservation.taches || [])[0]) ? (
                    <>
                      <div className="rounded-xl border border-honey-200/90 bg-gradient-to-br from-honey-50 via-white to-honey-50/40 px-4 py-3 shadow-sm">
                        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-honey-900/75">Montant (quantité effective)</p>
                            <p className="text-xs text-slate-600 mt-0.5">Calcul selon les tarifs miellerie AMAIR.</p>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            {previewMontantLoading ? (
                              <p className="text-sm font-medium text-slate-500">Calcul…</p>
                            ) : previewMontantTotal != null ? (
                              <p className="text-2xl font-bold text-slate-900 tabular-nums">
                                {Number(previewMontantTotal).toFixed(2)}{' '}
                                <span className="text-lg font-semibold text-slate-600">€</span>
                              </p>
                            ) : (
                              <p className="text-sm text-slate-500">Saisissez une quantité valide</p>
                            )}
                          </div>
                        </div>
                        {(previewDejaPayeEnLigne ||
                          (['stripe', 'solde'].includes(gestionReservation?.paiement?.methode) &&
                            Number(gestionReservation?.montantPrepaye) > 0)) && (
                          <div className="mt-3 pt-3 border-t border-honey-200/80 text-sm text-slate-700 space-y-1">
                            <p>
                              Déjà payé
                              {gestionReservation?.paiement?.methode === 'solde'
                                ? ' (avoir)'
                                : ' en ligne'}
                              :{' '}
                              <span className="font-semibold tabular-nums">
                                {Number(
                                  previewMontantPrepaye || gestionReservation?.montantPrepaye || 0
                                ).toFixed(2)}{' '}
                                €
                              </span>
                            </p>
                            {previewEcart != null && previewEcart > 0 && (
                              <p className="text-honey-950 font-medium">
                                Complément dû : {Number(previewEcart).toFixed(2)} €
                              </p>
                            )}
                            {previewEcart != null && previewEcart < 0 && (
                              <p className="text-green-800 font-medium">
                                Avoir à créditer sur le solde : {Math.abs(Number(previewEcart)).toFixed(2)} €
                              </p>
                            )}
                            {previewEcart === 0 && (
                              <p className="text-green-800">Soldé : aucun écart avec le prépaiement.</p>
                            )}
                          </div>
                        )}
                      </div>
                      {(() => {
                        const dejaPaye =
                          previewDejaPayeEnLigne ||
                          (gestionReservation?.paiement?.statut === 'paye' &&
                            ['stripe', 'solde'].includes(gestionReservation?.paiement?.methode) &&
                            Number(gestionReservation?.montantPrepaye) > 0);
                        const ecart = previewEcart;
                        if (dejaPaye && previewMontantLoading) {
                          return (
                            <p className="text-sm text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              Calcul de l&apos;écart avec le prépaiement…
                            </p>
                          );
                        }
                        if (dejaPaye && ecart != null && ecart <= 0) {
                          return (
                            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              {ecart < 0
                                ? 'Un avoir sera automatiquement crédité sur le solde de l’adhérent à la confirmation.'
                                : 'Aucun règlement complémentaire à saisir.'}
                            </p>
                          );
                        }
                        return (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                        <p className="text-sm font-medium text-slate-900">
                          {dejaPaye && ecart != null && ecart > 0
                            ? `Règlement du complément (${Number(ecart).toFixed(2)} €)`
                            : 'Règlement'}
                        </p>
                        <div className="flex flex-col gap-2">
                          {[
                            { value: 'espece', label: 'Réglé — espèces' },
                            { value: 'cheque', label: 'Réglé — chèque' },
                            { value: 'dette', label: 'Mettre en dette (ajouté au solde de l\'adhérent)' },
                          ].map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="radio" name="gc-mode-paiement" checked={step3ModePaiement === value}
                                onChange={() => setStep3ModePaiement(value)} disabled={gestionSubmitting}
                                className="text-honey-600"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                        {step3ModePaiement === 'cheque' && (
                          <div>
                            <label htmlFor="gc-ref-cheque" className="block text-sm text-slate-700 mb-1">Nº chèque (optionnel)</label>
                            <input id="gc-ref-cheque" type="text" value={step3RefCheque}
                              onChange={(e) => setStep3RefCheque(e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              disabled={gestionSubmitting}
                            />
                          </div>
                        )}
                        {(step3ModePaiement === 'espece' || step3ModePaiement === 'cheque') && (
                          <div>
                            <label htmlFor="gc-note-paiement" className="block text-sm text-slate-700 mb-1">Note complémentaire (optionnel)</label>
                            <input id="gc-note-paiement" type="text" value={step3NotePaiement}
                              onChange={(e) => setStep3NotePaiement(e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              disabled={gestionSubmitting}
                            />
                          </div>
                        )}
                      </div>
                        );
                      })()}
                    </>
                  ) : (
                    <p className="text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                      Pour ce type de prestation, aucun tarif n&apos;est facturé : la réservation sera marquée comme réalisée sans enregistrement de paiement.
                    </p>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
                    <button type="button" onClick={closeGestionReservation} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                      Fermer
                    </button>
                    <button type="button" onClick={confirmerRealisationPrestation} disabled={gestionSubmitting}
                      className="flex-1 px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:opacity-50 font-medium">
                      {gestionSubmitting ? 'Enregistrement…' : 'Enregistrer la prestation'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {showCautionFromGestion && gestionReservation && (
        <Portal>
          <div
            className={OVERLAY_CLASS}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeCautionFromGestion();
            }}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
              role="dialog"
              aria-labelledby="caution-recue-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
                <div>
                  <h3 id="caution-recue-title" className="text-base font-semibold text-slate-900">
                    Marquer la caution comme reçue
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {gestionReservation.usager
                      ? `${gestionReservation.usager.prenom || ''} ${gestionReservation.usager.nom || ''}`.trim()
                      : '—'}
                    <span className="ml-1.5 text-honey-800 font-medium">
                      ({gestionReservation.cautionMontant || 300} €)
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeCautionFromGestion}
                  disabled={cautionSubmitting}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {cautionError && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {cautionError}
                  </p>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Date de réception
                  </label>
                  <input
                    type="date"
                    value={cautionForm.dateReception}
                    onChange={(e) =>
                      setCautionForm((p) => ({ ...p, dateReception: e.target.value }))
                    }
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Note (optionnelle)
                  </label>
                  <input
                    type="text"
                    value={cautionForm.note}
                    onChange={(e) => setCautionForm((p) => ({ ...p, note: e.target.value }))}
                    placeholder="Ex. chèque n° 123456…"
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-1 focus:ring-honey-400 focus:border-honey-400 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Document justificatif (optionnel)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) =>
                      setCautionForm((p) => ({ ...p, document: e.target.files[0] || null }))
                    }
                    className="w-full text-xs text-slate-600 file:mr-2 file:py-1 file:px-2.5 file:rounded file:border-0 file:text-xs file:font-medium file:bg-honey-100 file:text-honey-800 hover:file:bg-honey-200"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={closeCautionFromGestion}
                  disabled={cautionSubmitting}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleMarkCautionRecu}
                  disabled={cautionSubmitting}
                  className="px-4 py-2 bg-honey-600 hover:bg-honey-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {cautionSubmitting ? 'Enregistrement…' : 'Enregistrer la réception'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Modale confirmation retrait (plage sans réservation — pas de window.confirm) */}
      {confirmRetraitPlageSlot && (
        <Portal>
        <div
          className={OVERLAY_CLASS}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeConfirmRetraitPlageModal();
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-labelledby="retrait-plage-sans-resa-title"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <h3 id="retrait-plage-sans-resa-title" className="text-lg font-semibold text-slate-900">
                Retirer la plage d&apos;ouverture
              </h3>
              <button
                type="button"
                onClick={closeConfirmRetraitPlageModal}
                disabled={retraitSansResaSubmitting}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                Cette plage n&apos;a aucune réservation active. Elle sera supprimée définitivement du
                calendrier.
              </p>
              <p className="text-sm text-slate-600 tabular-nums">
                Plage :{' '}
                <span className="font-medium text-slate-800">
                  {(() => {
                    const iso = jourISO(confirmRetraitPlageSlot);
                    if (!iso) return '—';
                    return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    });
                  })()}{' '}
                  · {debutPlage(confirmRetraitPlageSlot)} – {finPlage(confirmRetraitPlageSlot)}
                </span>
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeConfirmRetraitPlageModal}
                  disabled={retraitSansResaSubmitting}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={confirmRetraitPlageSansReservations}
                  disabled={retraitSansResaSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {retraitSansResaSubmitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Retrait…
                    </>
                  ) : (
                    'Retirer la plage'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Modale annulation plage (réservations actives : motif obligatoire + e-mails) */}
      {cancelPlageTarget && (
        <Portal>
        <div className={OVERLAY_CLASS}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-labelledby="cancel-plage-title"
            aria-modal="true"
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <h3 id="cancel-plage-title" className="text-lg font-semibold text-slate-900">
                Annuler la plage d&apos;ouverture
              </h3>
              <button
                type="button"
                onClick={closeCancelPlageModal}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={confirmAnnulationAvecReservations} className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                {(Number(cancelPlageTarget.reservationsActivesCount) || 0) === 1
                  ? '1 réservation active sera annulée.'
                  : `${Number(cancelPlageTarget.reservationsActivesCount) || 0} réservations actives seront annulées.`}{' '}
                Les usagers concernés recevront un e-mail avec le motif indiqué.
              </p>
              <p className="text-sm text-slate-600 tabular-nums">
                Plage :{' '}
                <span className="font-medium text-slate-800">
                  {(() => {
                    const iso = jourISO(cancelPlageTarget);
                    if (!iso) return '—';
                    return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    });
                  })()}{' '}
                  · {debutPlage(cancelPlageTarget)} – {finPlage(cancelPlageTarget)}
                </span>
              </p>
              <div>
                <label htmlFor="annulation-motif" className="block text-sm font-medium text-slate-700 mb-2">
                  Motif d&apos;annulation <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="annulation-motif"
                  value={annulationMotif}
                  onChange={(e) => setAnnulationMotif(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500 text-sm"
                  placeholder="Ex. : fermeture exceptionnelle, panne, etc."
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCancelPlageModal}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Fermer
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700"
                >
                  Confirmer l&apos;annulation
                </button>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}

      {/* Modal plage d'ouverture */}
      {showModal && (
        <Portal>
        <div className={`${OVERLAY_CLASS} z-50`}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingSlot ? 'Modifier la plage d\'ouverture' : 'Nouvelle plage d\'ouverture'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {canAssignResponsable && !editingSlot && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Responsable pendant cette ouverture *
                </label>
                <select
                  value={responsableUserId}
                  onChange={(e) => setResponsableUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                  required
                >
                  <option value="">— Choisir un responsable —</option>
                  {listeResponsables.map((r) => {
                    const uid = r.user?._id || r.user;
                    return (
                      <option key={r._id} value={String(uid)}>
                        {r.user.prenom} {r.user.nom} ({r.user.email})
                      </option>
                    );
                  })}
                </select>
                {listeResponsables.length === 0 && (
                  <p className="text-sm text-honey-800 mt-2">
                    Aucun responsable actif pour cette année. Ajoutez-en depuis l’onglet « Responsables ».
                  </p>
                )}
              </div>
            )}

            {editingSlot?.responsable && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <span className="font-medium text-slate-800">Responsable : </span>
                {editingSlot.responsable.prenom} {editingSlot.responsable.nom}
              </div>
            )}

              <div>
                <span className="block text-sm font-medium text-slate-700 mb-2">
                  Prestations proposées sur cette plage
                </span>
                <p className="text-sm text-slate-500 mb-3">
                  Cochez les prestations disponibles pendant cette ouverture. Sans liste renseignée
                  (anciennes plages), toutes restent réservables côté adhérents.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                  {TACHES_SERVICES.map((t) => {
                    const indisponible = t.disponible === false;
                    return (
                    <label
                      key={t.value}
                      title={t.title}
                      className={`flex items-center gap-2 text-sm text-slate-800 ${
                        indisponible ? 'hidden' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-honey-600 focus:ring-honey-500"
                        checked={!indisponible && formData.servicesOuverts.includes(t.value)}
                        disabled={indisponible}
                        onChange={(e) => {
                          if (indisponible) return;
                          const checked = e.target.checked;
                          setFormData((prev) => {
                            const next = new Set(prev.servicesOuverts || []);
                            if (checked) next.add(t.value);
                            else next.delete(t.value);
                            return { ...prev, servicesOuverts: [...next] };
                          });
                        }}
                      />
                      {t.label}
                    </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  min={todayISOLocal()}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Heure de début
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      value={parseHeureQuarts(formData.heureDebut).h}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          heureDebut: joinHeure(
                            e.target.value,
                            parseHeureQuarts(formData.heureDebut).m
                          ),
                        })
                      }
                      className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                      required
                    >
                      {HEURES_OPTIONS.map((hh) => (
                        <option key={hh} value={hh}>
                          {hh}
                        </option>
                      ))}
                    </select>
                    <span className="text-slate-500">:</span>
                    <select
                      value={parseHeureQuarts(formData.heureDebut).m}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          heureDebut: joinHeure(
                            parseHeureQuarts(formData.heureDebut).h,
                            e.target.value
                          ),
                        })
                      }
                      className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                      required
                    >
                      {MINUTES_QUARTS.map((mm) => (
                        <option key={mm} value={mm}>
                          {mm}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Heure de fin
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      value={parseHeureQuarts(formData.heureFin).h}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          heureFin: joinHeure(
                            e.target.value,
                            parseHeureQuarts(formData.heureFin).m
                          ),
                        })
                      }
                      className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                      required
                    >
                      {HEURES_OPTIONS.map((hh) => (
                        <option key={`fin-${hh}`} value={hh}>
                          {hh}
                        </option>
                      ))}
                    </select>
                    <span className="text-slate-500">:</span>
                    <select
                      value={parseHeureQuarts(formData.heureFin).m}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          heureFin: joinHeure(
                            parseHeureQuarts(formData.heureFin).h,
                            e.target.value
                          ),
                        })
                      }
                      className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                      required
                    >
                      {MINUTES_QUARTS.map((mm) => (
                        <option key={`finm-${mm}`} value={mm}>
                          {mm}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Notes (optionnel)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                  placeholder="Informations complémentaires..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors"
                >
                  {editingSlot ? 'Enregistrer' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}
